// Installing the app on a phone or a computer.
//
// Chrome and Edge fire `beforeinstallprompt` when they decide a site is
// installable, and they fire it once, early - usually before anybody has
// navigated to the settings page. So the event is caught here at module load
// and held, and the settings page subscribes to what was found rather than
// listening for itself.
//
// Safari never fires it. On an iPhone, installing is a manual step through the
// share sheet, so there the honest thing is to say so rather than show a button
// that cannot work.

const listeners = new Set();

let deferredPrompt = null;
let installed = false;

const browser = typeof window !== 'undefined';

const standalone = () =>
  browser &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches ||
    // Safari's own flag, which predates the standard one.
    window.navigator.standalone === true);

const isIos = () =>
  browser && /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

/**
 * 'installed'    - already running as an app, or the browser told us it was added
 * 'ready'        - the browser has offered a prompt we can fire from a button
 * 'ios'          - iPhone or iPad: tell them where the Share button is
 * 'unsupported'  - no prompt available; explain the browser's own menu
 */
export const installState = () => {
  if (!browser) return { status: 'unsupported' };
  if (installed || standalone()) return { status: 'installed' };
  if (deferredPrompt) return { status: 'ready' };
  if (isIos()) return { status: 'ios' };
  return { status: 'unsupported' };
};

const announce = () => {
  const state = installState();
  listeners.forEach((listener) => listener(state));
};

export const subscribeInstallState = (listener) => {
  listeners.add(listener);
  listener(installState());
  return () => listeners.delete(listener);
};

/** Opens the browser's own install dialog. Resolves to whether they accepted. */
export const promptInstall = async () => {
  if (!deferredPrompt) return false;

  const prompt = deferredPrompt;
  // A prompt can only be used once, so it is dropped before awaiting the
  // answer: a second click while the dialog is open must not reuse it.
  deferredPrompt = null;
  announce();

  prompt.prompt();
  const { outcome } = await prompt.userChoice;

  if (outcome === 'accepted') {
    installed = true;
  } else {
    // They said no. Put it back so they can change their mind without a reload.
    deferredPrompt = prompt;
  }
  announce();

  return outcome === 'accepted';
};

if (browser) {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this the browser shows its own mini-infobar and never hands the
    // prompt over, so the button in settings would have nothing to fire.
    event.preventDefault();
    deferredPrompt = event;
    announce();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    announce();
  });
}

/**
 * Registers the worker that makes the app installable and lets it open offline.
 * Only in a real build: in dev the worker would sit in front of Vite's own
 * module serving and make hot reloading behave strangely for no gain.
 */
export const registerServiceWorker = () => {
  if (!browser || !('serviceWorker' in navigator)) return;
  if (!import.meta.env.PROD) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.error('Service worker registration failed:', error);
    });
  });
};
